import { useCallback, useContext, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import type { Chat } from '@trinity/chat';
import type { KolSearchRecommendation } from '@trinity/chat';
import {
  DispatchKolSearchFormContext,
  KolSearchStatusContext,
  KolSearchSubmitContext,
  KolSearchFormContext,
  KolSearchResultsContext,
  KolSearchCancelContext,
  ActiveChatSessionContext,
  KolProfileContext,
  UpdateKolSearchProfileContext,
  KolSearchErrorTraceIdContext,
  KolFollowupQuestionsContext,
  KolSearchFormBeginContext,
  KolSearchRecommendationsContext,
  KolSearchRecommendationProfileSelectedContext,
  KolResetSearchContext,
  CitationsContext,
  KolSearchIdentifierContext,
  KolSearchIdContext,
} from './context';
import { useScrollToBottomOfMessages } from './hooks/useScrollMessageList';
import type { Context } from './interfaces';

type Props = {
  children: React.ReactNode;
  item: Chat.KolSearchItem;
};

type UpdateProfileParams = {
  kolId: number;
  kolSearchId: number;
};

export const KolSearchContainer = (props: Props) => {

  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const scrollToBottom = useScrollToBottomOfMessages();
  const brandIdentifier = useActiveBrandIdentifier();

  const apiClient = useChatApiClient();

  const updateProfileMutation = useUpdateProfileMutation({
    identifier: props.item.identifier,
  });
  const submitFormMutation = useMutation({
    mutationFn: () => {
      return apiClient.kolSearch.submitKolSearch({
        chatIdentifier: chat.identifier,
        identifier: props.item.identifier,
        form: props.item.form,
        recommendations: props.item.recommendations,
        brandIdentifier,
        filters: [],
        sort: null,
      });
    },
    onMutate: () => {
      dispatch({
        type: 'kol-search-submitted',
        payload: {
          chatIdentifier: chat.identifier,
          identifier: props.item.identifier,
          brandIdentifier,
        },
      });
      scrollToBottom({ nthFromBottom: 1 });
    },
    onError: err => {
      const error = err as AxiosError;
      const traceId = error.response?.headers?.['X-Sentry-Trace-Id'] as string;
      dispatch({
        type: 'kol-search-form-submission-errored',
        payload: {
          identifier: props.item.identifier,
          traceId,
        },
      });
    },
    onSuccess: res => {
      const selectedKolId = props.item.form.kolId;
      if (selectedKolId) {
        return updateProfileMutation.mutateAsync({
          kolSearchId: res.result.id,
          kolId: selectedKolId,
        });
      }
      else {
        dispatch({
          type: 'kol-search-results-available',
          payload: {
            chatInstance: res.chatInstance,
            result: res.result,
            identifier: props.item.identifier,
          },
        });
      }

      scrollToBottom({ nthFromBottom: 1 });
    },
  });

  const handleFormSubmit = useCallback(() => {
    submitFormMutation.mutateAsync();
  }, [submitFormMutation]);

  const cancelMutation = useMutation({
    mutationFn: () => apiClient.kolSearch.cancelKolSearch({
      chatIdentifier: chat.identifier,
      kolSearchId: props.item.id,
    }),
    onSuccess: () => {
      dispatch({
        type: 'kol-search-cancelled',
        payload: {
          identifier: props.item.identifier,
        },
      });
      scrollToBottom({ nthFromBottom: 1 });
    },
  });

  const handleSearchCancelled = useCallback(() => {
    cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const handleProfileSelected = useCallback((data: { id: number; name: string }) => {
    updateProfileMutation.mutateAsync({
      kolId: data.id,
      kolSearchId: props.item.id,
    });
  }, [props.item.id, updateProfileMutation]);

  const handleFormUpdated = useCallback((form: Partial<Context.KolSearchFormState>) => {
    dispatch({
      type: 'kol-search-form-updated',
      payload: {
        form,
        identifier: props.item.identifier,
      },
    });
  }, [dispatch, props.item.identifier]);

  const handleFormBegin = useCallback(() => {
    dispatch({
      type: 'kol-search-form-begin',
      payload: {
        identifier: props.item.identifier,
      },
    });
    scrollToBottom({ nthFromBottom: 1 });
  }, [dispatch, scrollToBottom, props.item.identifier]);

  const handleRecommendationProfileSelected = useCallback(async (kol: KolSearchRecommendation) => {
    const search = await apiClient.kolSearch.submitKolSearch({
      chatIdentifier: chat.identifier,
      identifier: props.item.identifier,
      form: {
        kolId: kol.id,
        name: kol.name,
      },
      recommendations: {
        status: 'loaded',
        items: props.item.recommendations.items,
        total: props.item.recommendations.total,
      },
      brandIdentifier,
      filters: [],
      sort: null,
    });

    dispatch({
      type: 'kol-search-profile-selected',
      payload: {
        identifier: props.item.identifier,
      },
    });

    // Update form state with selected profile name for the user message
    dispatch({
      type: 'kol-search-form-updated',
      payload: {
        form: {
          kolId: kol.id,
          name: kol.name,
        },
        identifier: props.item.identifier,
      },
    });
    scrollToBottom({ nthFromBottom: 1 });

    await updateProfileMutation.mutateAsync({
      kolSearchId: search.result.id,
      kolId: kol.id,
    });
  }, [chat.identifier, props.item.identifier, props.item.recommendations.items, props.item.recommendations.total, brandIdentifier, dispatch, updateProfileMutation, scrollToBottom]);

  const handleNewSearch = useCallback(() => {
    dispatch({
      type: 'kol-search/new-search-initiated',
      payload: {
        oldIdentifier: props.item.identifier,
      },
    });
    scrollToBottom({ nthFromBottom: 1 });
  }, [dispatch, props.item.identifier, scrollToBottom]);

  const getRecommendationsMutation = useMutation({
    mutationFn: () => apiClient.kolSearch.getKolSearchRecommendations({
      afterKolId: null,
      pageSize: 25,
      filters: [],
      sortBy: null,
      sortDir: null,
      brandIdentifier,
    }),
    onMutate: () => {
      dispatch({
        type: 'kol-search/recommendations-loading',
        payload: {
          identifier: props.item.identifier,
        },
      });
    },
    onSettled: (res, _error, _vars) => {
      dispatch({
        type: 'kol-search/recommendations-loaded',
        payload: {
          identifier: props.item.identifier,
          items: res.items,
          total: res.total,
        },
      });
      scrollToBottom({ nthFromBottom: 1 });
    },
  });

  useEffect(() => {
    if (!getRecommendationsMutation.isPending && (!props.item.recommendations || props.item.recommendations.status === 'uninitialized')) {
      getRecommendationsMutation.mutateAsync();
    }
  }, [props.item.recommendations, getRecommendationsMutation]);

  return (
    <UpdateKolSearchProfileContext.Provider value={handleProfileSelected}>
      <KolProfileContext.Provider value={props.item.profile}>
        <KolSearchCancelContext.Provider value={handleSearchCancelled}>
          <KolSearchFormContext.Provider value={props.item.form}>
            <DispatchKolSearchFormContext.Provider value={handleFormUpdated}>
              <KolSearchFormBeginContext.Provider value={handleFormBegin}>
                <KolSearchStatusContext.Provider value={props.item.status}>
                  <KolSearchSubmitContext.Provider value={handleFormSubmit}>
                    <KolSearchResultsContext.Provider value={props.item.results}>
                      <KolSearchErrorTraceIdContext.Provider value={props.item.errorTraceId}>
                        <KolFollowupQuestionsContext.Provider value={props.item.followupQuestions}>
                          <KolSearchRecommendationProfileSelectedContext.Provider value={handleRecommendationProfileSelected}>
                            <KolSearchRecommendationsContext.Provider value={props.item.recommendations}>
                              <KolResetSearchContext.Provider value={handleNewSearch}>
                                <CitationsContext.Provider value={props.item.citations}>
                                  <KolSearchIdentifierContext.Provider value={props.item.identifier}>
                                    <KolSearchIdContext.Provider value={props.item.id}>
                                      {props.children}
                                    </KolSearchIdContext.Provider>
                                  </KolSearchIdentifierContext.Provider>
                                </CitationsContext.Provider>
                              </KolResetSearchContext.Provider>
                            </KolSearchRecommendationsContext.Provider>
                          </KolSearchRecommendationProfileSelectedContext.Provider>
                        </KolFollowupQuestionsContext.Provider>
                      </KolSearchErrorTraceIdContext.Provider>
                    </KolSearchResultsContext.Provider>
                  </KolSearchSubmitContext.Provider>
                </KolSearchStatusContext.Provider>
              </KolSearchFormBeginContext.Provider>
            </DispatchKolSearchFormContext.Provider>
          </KolSearchFormContext.Provider>
        </KolSearchCancelContext.Provider>
      </KolProfileContext.Provider>
    </UpdateKolSearchProfileContext.Provider>
  );
};



export const useUpdateProfileMutation = (params: { identifier: string }) => {
  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const scrollToBottom = useScrollToBottomOfMessages();

  const apiClient = useChatApiClient();

  const updateProfileMutation = useMutation({
    mutationFn: ({ kolId, kolSearchId }: UpdateProfileParams) => {
      return apiClient.kolSearch.updateKolSearchProfile({
        chatIdentifier: chat.identifier,
        kolId,
        kolSearchId,
      });
    },
    onMutate: () => {
      dispatch({
        type: 'kol-search-profile-selected',
        payload: {
          identifier: params.identifier,
        },
      });
      scrollToBottom({ nthFromBottom: 1 });
    },
    onError: err => {
      const error = err as AxiosError;
      const traceId = error.response?.headers?.['X-Sentry-Trace-Id'] as string;
      dispatch({
        type: 'kol-search-profile-selection-errored',
        payload: {
          identifier: params.identifier,
          traceId,
        },
      });
    },
    onSuccess: res => {
      setTimeout(() => {
        dispatch({
          type: 'kol-search-profile-available',
          payload: {
            chatInstance: res.chatInstance,
            result: res.result,
            identifier: params.identifier,
          },
        });
        scrollToBottom({ nthFromBottom: 1 });
      }, 1000);
    },
  });

  return updateProfileMutation;
};